import type {
	PropsWithChildren,
	MutableRefObject,
	ChangeEvent,
	DragEventHandler,
} from 'react';
import { Fragment, useState, useCallback } from 'react';
import styled from 'styled-components';

import UUID from 'pkg/uuid';

// 1px width/height to fix a bug in Safari
const Input = styled.input`
	opacity: 0;
	width: 1px;
	height: 1px;
`;

interface FileInputProps extends PropsWithChildren {
	multiple?: boolean;
	accept?: string;
	onChange: (files: File[]) => void;
	className?: string;
	allowDrop?: boolean;
	inputRef?: MutableRefObject<HTMLInputElement>;
}

const FileInput = ({
	children,
	onChange,
	className,
	allowDrop = false,
	multiple = true,
	accept = 'image/*',
	inputRef,
}: FileInputProps) => {
	const id = `file-input-${UUID()}`;

	const [willDrop, setWillDrop] = useState(false);

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;

		if (files && files.length > 0) {
			onChange(Array.from(files));
		}
	};

	const handleDrag: DragEventHandler<HTMLLabelElement> = (event) => {
		event.preventDefault();
		event.stopPropagation();
	};

	const handleDragEnter: DragEventHandler<HTMLLabelElement> = useCallback(
		(event) => {
			event.preventDefault();
			event.stopPropagation();

			const items = event.dataTransfer?.items;

			if (items && items.length > 0) {
				setWillDrop(true);
			}
		},
		[setWillDrop]
	);

	const handleDragLeave: DragEventHandler<HTMLLabelElement> = useCallback(
		(event) => {
			event.preventDefault();
			event.stopPropagation();

			setWillDrop(false);
		},
		[setWillDrop]
	);

	const handleDrop: DragEventHandler<HTMLLabelElement> = useCallback(
		(event) => {
			event.preventDefault();
			event.stopPropagation();

			const files = event.dataTransfer?.files;

			if (files && files.length > 0) {
				onChange(Array.from(files));
				setWillDrop(false);
			}
		},
		[setWillDrop, onChange]
	);

	const dropProps = allowDrop
		? {
				onDrop: handleDrop,
				onDragOver: handleDrag,
				onDragEnter: handleDragEnter,
				onDragLeave: handleDragLeave,
			}
		: {};

	return (
		<Fragment>
			<label
				htmlFor={id}
				data-will-drop={willDrop}
				className={className}
				{...dropProps}>
				{children}
			</label>

			<Input
				ref={inputRef}
				type="file"
				id={id}
				multiple={multiple}
				accept={accept}
				onChange={handleFileChange}
			/>
		</Fragment>
	);
};

export default FileInput;
